(() => {
  class VariantPicker extends HTMLElement {
    getVariants() {
      if (!this.variants) {
        const data = this.querySelector('script[type="application/json"]');
        this.variants = data ? JSON.parse(data.textContent) : [];
      }
      return this.variants;
    }

    getOptionValues() {
      return Array.from(this.querySelectorAll('.product-form__input')).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input[type="radio"]:checked')?.value;
      });
    }

    findVariant(values) {
      return this.getVariants().find(
        (variant) => variant.options?.length === values.length && variant.options.every((value, index) => value === values[index])
      );
    }

    updateAvailability() {
      const selectedValues = this.getOptionValues();
      const variants = this.getVariants();
      Array.from(this.querySelectorAll('.product-form__input')).forEach((group, index) => {
        const choices = Array.from(group.querySelectorAll('option, input[type="radio"]'));
        choices.forEach((choice) => {
          const matches = variants.filter(
            (variant) => variant.options[index] === choice.value && variant.options.slice(0, index).every((value, position) => value === selectedValues[position])
          );
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);
          const target = choice.closest('.color-swatch') || choice;
          target.classList.toggle('non-existent', !exists);
          target.classList.toggle('unavailable', exists && !available);
          choice.classList.toggle('disabled', exists && !available);
          if (choice.tagName === 'OPTION') {
            choice.disabled = !exists || !available;
            choice.hidden = !exists;
          }
        });
      });
    }
  }

  if (!customElements.get('card-variant-selects')) {
    customElements.define(
      'card-variant-selects',
      class CardVariantSelects extends VariantPicker {
        constructor() {
          super();
          this.addEventListener('change', (event) => {
            if (!event.target.matches('input[type="radio"], select')) return;
            this.updateAvailability();
            const variant = this.findVariant(this.getOptionValues());
            const form = document.getElementById(this.dataset.section);
            const input = form?.querySelector('input[name="id"]');
            const button = form?.querySelector('[type="submit"]');
            if (input) {
              input.value = variant?.id || '';
              input.disabled = !variant;
            }
            if (button) button.disabled = !variant?.available;
          });
        }

        connectedCallback() {
          this.updateAvailability();
        }
      }
    );
  }

  class SecondaryVariantSelect extends VariantPicker {
    constructor() {
      super();
      this.addEventListener('change', (event) => {
        if (!event.target.matches('select')) return;
        this.updateAvailability();
        const variant = this.getSelectedVariant(event.target);
        this.updateProduct(variant);
      });
    }

    getSelectedVariant(changedSelect) {
      if (changedSelect.classList.contains('variant-dropdown')) {
        const option = changedSelect.selectedOptions[0];
        return this.getVariants().find((variant) => String(variant.id) === option?.dataset.variantId);
      }
      return this.findVariant(this.getOptionValues());
    }

    updateProduct(variant) {
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      const input = form?.querySelector('input[name="id"]');
      const button = document.getElementById(`SectionAtcBtn-${this.dataset.section}`);
      if (input) input.value = variant?.id || '';
      if (button) button.disabled = !variant?.available;
      this.querySelectorAll('.sticky-atc__error').forEach((error) => (error.style.display = 'none'));

      if (variant) {
        document.querySelectorAll(`.main-atc-price-span-${this.dataset.section}`).forEach((price) => {
          price.textContent = window.formatMoney(variant.price, window.moneyFormat);
        });
        const image = document.getElementById(`sticky-atc-image-${this.dataset.section}`);
        if (image && variant.featured_image?.src) image.src = variant.featured_image.src;
      }

      const primary = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
      let primaryInput;
      primary?.querySelectorAll('.product-form__input').forEach((group, index) => {
        const value = variant?.options?.[index];
        const select = group.querySelector('select');
        if (select) {
          select.value = value;
          primaryInput = select;
          return;
        }
        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        if (radio) {
          radio.checked = true;
          primaryInput = radio;
        }
      });
      primaryInput?.dispatchEvent(new Event('change', { bubbles: true }));
      window.publish?.(window.PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.section, variant } });
    }
  }

  if (!customElements.get('secondary-variant-select')) customElements.define('secondary-variant-select', SecondaryVariantSelect);
  if (!customElements.get('secondary-variant-select-separate')) {
    customElements.define(
      'secondary-variant-select-separate',
      class SecondaryVariantSelectSeparate extends SecondaryVariantSelect {}
    );
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyAtc extends HTMLElement {
        constructor() {
          super();
          this.updateVisibility = this.updateVisibility.bind(this);
          this.addEventListener('click', (event) => {
            if (!event.target.closest('.sticky-atc__scroll-btn')) return;
            event.preventDefault();
            const destination = this.dataset.scrollDestination;
            const target = destination && document.getElementById(destination);
            target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }

        connectedCallback() {
          window.addEventListener('scroll', this.updateVisibility, { passive: true });
          this.updateVisibility();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.updateVisibility);
        }

        updateVisibility() {
          const mode = this.dataset.displayMode;
          const threshold = Number(this.dataset.displayThreshold || 0);
          const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
          const show =
            mode === 'always' ||
            (mode === 'after_pixels' && window.scrollY >= threshold) ||
            (mode === 'after_percent' && (window.scrollY / maxScroll) * 100 >= threshold) ||
            (mode === 'after_scroll' && window.scrollY > 0);
          this.classList.toggle('active', show);
        }
      }
    );
  }

  if (!customElements.get('resdor-subscription-widget')) {
    customElements.define(
      'resdor-subscription-widget',
      class ResdorSubscriptionWidget extends HTMLElement {
        constructor() {
          super();
          this.addEventListener('click', (event) => {
            const option = event.target.closest('.subscription__box');
            if (option) this.select(option.dataset.type);
          });
          this.addEventListener('change', (event) => {
            if (event.target.matches('[data-selling-plan-id], select')) {
              this.select('subscription');
              this.updatePrices();
            }
          });
        }

        connectedCallback() {
          const selected = this.querySelector('.subscription__box[data-selected="true"]')?.dataset.type;
          this.select(selected || 'one_time', false);
        }

        selectedPlanOption() {
          return this.querySelector('select [data-selling-plan-id]:checked, input[data-selling-plan-id]:checked');
        }

        selectedPlanId() {
          const selected = this.selectedPlanOption();
          return selected?.dataset.sellingPlanId || this.dataset.firstSellingPlanId;
        }

        updatePrices() {
          if (this.dataset.updatePrices !== 'true') return;
          const selected = this.selectedPlanOption();
          if (!selected) return;

          const price = Number(selected.dataset.price || 0);
          const comparePrice = Number(selected.dataset.comparePrice || 0);
          const amountSaved = Math.max(0, comparePrice - price);
          const amountSavedRounded = Math.round(amountSaved / 100) * 100;
          const percentageSaved = comparePrice > 0 ? Math.round((amountSaved / comparePrice) * 100) : 0;
          const format = (cents) => window.formatMoney(cents, this.dataset.moneyFormat);
          const subscriptionName = this.dataset.subscriptionName || '';

          this.querySelectorAll('.sub-price-update[data-text]').forEach((element) => {
            const template = element.dataset.text;
            if (!template) return;
            element.innerHTML = template
              .replace(/\[subscription_name\]/g, subscriptionName)
              .replace(/\[price\]/g, format(price))
              .replace(/\[compare_price\]/g, format(comparePrice))
              .replace(/\[amount_saved_rounded\]/g, format(amountSavedRounded))
              .replace(/\[amount_saved\]/g, format(amountSaved))
              .replace(/\[percentage_saved\]/g, `${percentageSaved}%`);
          });
        }

        select(type, focus = true) {
          const isSubscription = type === 'subscription';
          this.querySelectorAll('.subscription__box').forEach((box) => {
            const selected = box.dataset.type === type;
            box.classList.toggle('subscription__box--selected', selected);
            box.dataset.selected = String(selected);
          });

          const form = document.getElementById(`product-form-${this.dataset.section}`);
          if (!form) return;
          let input = form.querySelector('input[name="selling_plan"]');
          if (isSubscription) {
            input ||= Object.assign(document.createElement('input'), { type: 'hidden', name: 'selling_plan' });
            input.value = this.selectedPlanId() || '';
            if (!input.parentElement) form.append(input);
          } else {
            input?.remove();
          }

          if (focus) this.querySelector(`.subscription__box[data-type="${type}"]`)?.focus?.();
        }
      }
    );
  }

  if (!customElements.get('quantity-breaks')) {
    customElements.define(
      'quantity-breaks',
      class QuantityBreaks extends HTMLElement {
        connectedCallback() {
          this.products = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
          this.variants = this.products.flatMap((product) => product.variants || []);
          this.caretMarkup = this.querySelector('.icon-caret')?.outerHTML || '';
          this.slotMemory = {};

          this.querySelectorAll('.quantity-break__selector-item').forEach((item) => {
            item.dataset.resolvedVariantId = item.dataset.selectedId || '';
          });

          this.addEventListener('change', (event) => {
            if (event.target.matches('input[type="radio"][name="quantity"]')) {
              this.onQuantityChange(event.target.value);
              return;
            }
            if (event.target.matches('[data-cross-product-select]')) {
              this.onSlotProductChange(event.target);
              return;
            }
            if (event.target.matches('.variant-dropdown')) {
              this.onSlotVariantChange(event.target);
            }
          });

          // "Enable variant price updates" (data-update-prices, merchant-facing
          // setting already on in this theme's product templates): keep tiers
          // that fall back to `basePrice` — i.e. any tier with no per-slot
          // picker of its own, like "Buy 1" — in sync with the *main* product's
          // own variant picker (Size, etc.), not just this widget's internal
          // cross-product/variant controls. `data-base-price` is otherwise a
          // one-time snapshot from page load, so without this, changing the
          // main Size picker left the bundle price stuck on the old variant
          // until a full page reload re-rendered the Liquid default.
          if (this.dataset.updatePrices === 'true') {
            window.subscribe?.(window.PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
              if (data?.sectionId !== this.dataset.section || !data.variant) return;
              this.dataset.basePrice = String(data.variant.price);
              this.updatePrices();
            });
          }

          this.updatePrices();
        }

        getProduct(id) {
          if (!id) return null;
          return this.products.find((product) => String(product.id) === String(id)) || null;
        }

        onQuantityChange(value) {
          const form = document.getElementById(`product-form-${this.dataset.section}`);
          const quantity = form?.querySelector('input[name="quantity"]');
          if (quantity) {
            quantity.value = value;
            quantity.dispatchEvent(new Event('change', { bubbles: true }));
            window.publish?.(window.PUB_SUB_EVENTS.quantityUpdate, {
              data: { sectionId: this.dataset.section, quantity: Number(value) },
            });
          }
          this.applySlotMemory();
          this.updatePrices();
        }

        onSlotVariantChange(select) {
          const item = select.closest('.quantity-break__selector-item');
          if (!item) return;
          this.resolveSlotVariant(item);
          this.rememberSlot(item);
          this.updatePrices();
        }

        onSlotProductChange(select) {
          const item = select.closest('.quantity-break__selector-item');
          const productData = this.getProduct(select.value);
          if (!item || !productData) return;

          this.applyProductToSlot(item, productData);
          this.rememberSlot(item);
          this.updatePrices();
          if (this.products[0] && String(productData.id) !== String(this.products[0].id)) {
            this.showProductPreview(productData);
          }
        }

        applyProductToSlot(item, productData) {
          item.dataset.activeProductId = String(productData.id);

          const optionsWrap = item.querySelector('[data-variant-options]');
          if (optionsWrap) {
            optionsWrap.innerHTML = '';
            (productData.options || []).forEach((optionName, index) => {
              const key = `option${index + 1}`;
              const values = [...new Set((productData.variants || []).map((variant) => variant[key]).filter(Boolean))];
              const wrap = document.createElement('div');
              wrap.className = `select no-background ${this.dataset.selectClasses || ''}`.trim();
              wrap.style.cssText = 'flex:0 0 auto;min-width:6rem';
              const optionSelect = document.createElement('select');
              optionSelect.className = 'quantity-break__variant-select select__select variant-dropdown';
              optionSelect.name = `options[${optionName}]`;
              values.forEach((value) => {
                const optionEl = document.createElement('option');
                optionEl.value = value;
                optionEl.textContent = value;
                optionSelect.appendChild(optionEl);
              });
              wrap.appendChild(optionSelect);
              if (this.caretMarkup) wrap.insertAdjacentHTML('beforeend', this.caretMarkup);
              optionsWrap.appendChild(wrap);
            });
          }

          const thumb = item.querySelector('[data-product-thumb]');
          if (thumb && productData.featured_image) {
            thumb.src = productData.featured_image;
          }

          this.resolveSlotVariant(item);
        }

        resolveSlotVariant(item) {
          // Self-heal: trust the live product-select value over the cached
          // activeProductId. If a prior product-change event ever failed to
          // fully apply (e.g. a missed change event), this re-syncs before
          // resolving instead of silently resolving against stale/wrong data.
          const productSelect = item.querySelector('[data-cross-product-select]');
          if (productSelect && productSelect.value && productSelect.value !== item.dataset.activeProductId) {
            const productData = this.getProduct(productSelect.value);
            if (productData) {
              this.applyProductToSlot(item, productData);
              return this.resolveVariant(item.dataset.resolvedVariantId);
            }
          }

          const activeProduct = this.getProduct(item.dataset.activeProductId) || this.products[0];
          const variants = activeProduct ? activeProduct.variants || [] : this.variants;
          const values = Array.from(item.querySelectorAll('.variant-dropdown')).map((el) => el.value);
          const variant =
            variants.find((candidate) => values.every((value, index) => candidate[`option${index + 1}`] === value)) ||
            variants.find((candidate) => candidate.available) ||
            variants[0];
          item.dataset.resolvedVariantId = variant ? String(variant.id) : '';
          return variant;
        }

        rememberSlot(item) {
          const index = item.dataset.selectIndex;
          if (index === undefined) return;
          this.slotMemory[index] = {
            productId: item.dataset.activeProductId,
            optionValues: Array.from(item.querySelectorAll('.variant-dropdown')).map((el) => el.value),
          };
        }

        applySlotMemory() {
          const label = this.getActiveLabel();
          if (!label) return;

          label.querySelectorAll('.quantity-break__selector-item').forEach((item) => {
            const memory = this.slotMemory[item.dataset.selectIndex];
            if (!memory) return;

            if (memory.productId && memory.productId !== item.dataset.activeProductId) {
              const productSelect = item.querySelector('[data-cross-product-select]');
              const productData = this.getProduct(memory.productId);
              const hasOption =
                productSelect && Array.from(productSelect.options).some((option) => option.value === memory.productId);
              if (productSelect && hasOption && productData) {
                productSelect.value = memory.productId;
                this.applyProductToSlot(item, productData);
              }
            }

            if (memory.optionValues?.length) {
              const selects = item.querySelectorAll('.variant-dropdown');
              selects.forEach((select, i) => {
                const value = memory.optionValues[i];
                if (value && Array.from(select.options).some((option) => option.value === value)) {
                  select.value = value;
                }
              });
              this.resolveSlotVariant(item);
            }
          });
        }

        showProductPreview(productData) {
          if (!productData.featured_image) return;
          const galleries = document.querySelectorAll(`media-gallery[data-section="${this.dataset.section}"]`);
          if (!galleries.length) return;
          const duration = Number(this.dataset.previewDuration || 4) * 1000;

          galleries.forEach((gallery) => {
            let overlay = gallery.querySelector('.quantity-break__product-preview-overlay');
            if (!overlay) {
              overlay = document.createElement('div');
              overlay.className = 'quantity-break__product-preview-overlay';
              overlay.innerHTML =
                '<img class="quantity-break__product-preview-overlay__image" alt="">' +
                '<span class="quantity-break__product-preview-overlay__title"></span>';
              overlay.addEventListener('click', () => overlay.classList.remove('is-visible'));
              if (!gallery.style.position) gallery.style.position = 'relative';
              gallery.appendChild(overlay);
            }
            overlay.querySelector('img').src = productData.featured_image;
            overlay.querySelector('img').alt = productData.title || '';
            overlay.querySelector('span').textContent = productData.title || '';
            overlay.classList.add('is-visible');

            clearTimeout(overlay.hideTimeoutId);
            overlay.hideTimeoutId = setTimeout(() => overlay.classList.remove('is-visible'), duration);
          });
        }

        resolveVariant(id) {
          if (!id) return null;
          return this.variants.find((variant) => String(variant.id) === String(id)) || null;
        }

        getActiveLabel() {
          const checked = this.querySelector('input[type="radio"][name="quantity"]:checked');
          if (!checked) return null;
          return this.querySelector(`label[data-input="${checked.dataset.input}"]`);
        }

        fallbackVariantId() {
          const form = document.getElementById(`product-form-${this.dataset.section}`);
          const currentId = form?.querySelector('input[name="id"]')?.value;
          return currentId || this.variants.find((variant) => variant.available)?.id;
        }

        getSelectedItems() {
          const label = this.getActiveLabel();
          const qty = label ? Number(label.dataset.quantity || 0) : 0;
          if (!qty) return [];

          const slots = label.querySelectorAll('.quantity-break__selector-item');
          const items = [];
          for (let index = 0; index < qty; index += 1) {
            const item = slots[index];
            const variant = item ? this.resolveSlotVariant(item) : null;
            const variantId = variant ? variant.id : this.fallbackVariantId();
            if (variantId) items.push({ id: Number(variantId), quantity: 1 });
          }

          // Gifts/add-ons: only ones rendered inside the *active* tier's own label
          // (already server-side filtered to be eligible for this tier) AND
          // currently selected (gift = always selected, checkbox = user-toggled).
          label.querySelectorAll('.quantity-break__upsell').forEach((upsell) => {
            if (upsell.dataset.selected !== 'true') return;
            const variantId = Number(upsell.dataset.product);
            if (variantId) items.push({ id: variantId, quantity: 1 });
          });

          return items;
        }

        updatePrices() {
          const label = this.getActiveLabel();
          if (!label) return;
          const qty = Number(label.dataset.quantity || 0);
          if (!qty) return;

          const percentageLeft = Number(label.dataset.percentageLeft || 1);
          const percentage = Math.round((1 - percentageLeft) * 100);
          const fixedDiscount = Number(label.dataset.fixedDiscount || 0);
          const compareMode = label.dataset.compareMode;
          const basePrice = Number(this.dataset.basePrice || 0);

          const slots = label.querySelectorAll('.quantity-break__selector-item');
          let corePrice = 0;
          let totalCompare = 0;
          for (let index = 0; index < qty; index += 1) {
            const item = slots[index];
            const variant = item ? this.resolveSlotVariant(item) : null;
            const unitPrice = variant ? variant.price : basePrice;
            // Each unit's own regular price is the correct "before discount"
            // reference for that unit — a single flat main-product compare
            // price would badly under/over-count once slots hold different
            // cross-product picks with different prices.
            const unitCompare =
              compareMode === 'compare_price' && variant?.compare_at_price > unitPrice
                ? variant.compare_at_price
                : unitPrice;
            // Shopify's real per-line Automatic Discount applies the percentage
            // to EACH cart line independently and floors the discount to the
            // cent (confirmed against real cart totals: 10% off a $129.99 line
            // charges $117.00/saves $12.99 — floor(12999*0.10)=1299, not a
            // round() of the discounted price, which would give $116.99).
            // Flooring once on the bundle's combined total instead can drift a
            // few cents from the real cart total once slots hold differently-
            // priced cross-product picks, since floor(sum) != sum(floor-each).
            const unitDiscount = Math.floor((unitPrice * percentage) / 100);
            corePrice += unitPrice - unitDiscount;
            totalCompare += unitCompare;
          }
          corePrice = Math.max(0, corePrice - fixedDiscount);

          // Selected paid add-ons are charged in full on top of the bundle —
          // they're extras, not part of the "Buy N" discount math, so they
          // must not be run through percentageLeft/fixedDiscount themselves.
          let upsellsTotal = 0;
          label.querySelectorAll('.quantity-break__upsell').forEach((upsell) => {
            if (upsell.dataset.selected === 'true') upsellsTotal += Number(upsell.dataset.price || 0);
          });

          const price = corePrice + upsellsTotal;
          // The displayed "before discount" reference must also carry the
          // add-on cost — add-ons are never discounted, so they add equally
          // to both sides. Without this, [compare_price] can end up *lower*
          // than [price] once a paid add-on is selected (a crossed-out price
          // that reads as cheaper than what the customer actually pays).
          const displayedComparePrice = totalCompare + upsellsTotal;
          const amountSaved = totalCompare - corePrice;
          const amountSavedRounded = Math.round(amountSaved / 100) * 100;
          const priceEach = Math.round(corePrice / qty);
          const compareEach = Math.round(totalCompare / qty);
          const format = (cents) => window.formatMoney(cents, this.dataset.moneyFormat);

          label.querySelectorAll('.variant-price-update[data-text]').forEach((element) => {
            const template = element.dataset.text;
            if (!template) return;
            element.innerHTML = template
              .replace(/\[quantity\]/g, qty)
              .replace(/\[price\]/g, format(price))
              .replace(/\[compare_price\]/g, format(displayedComparePrice))
              .replace(/\[amount_saved\]/g, format(amountSaved))
              .replace(/\[amount_saved_rounded\]/g, format(amountSavedRounded))
              .replace(/\[price_each\]/g, format(priceEach))
              .replace(/\[compare_price_each\]/g, format(compareEach));
          });

          // The "before discount" price element is server-rendered hidden/shown
          // once based on the default (no cross-product, no add-on) math — keep
          // it in sync as the customer's actual picks change what's being compared.
          label.querySelectorAll('.quantity-break__compare-price').forEach((element) => {
            element.classList.toggle('hidden', displayedComparePrice <= price);
          });
        }
      }
    );
  }

  if (!customElements.get('clickable-discount')) {
    customElements.define(
      'clickable-discount',
      class ClickableDiscount extends HTMLElement {
        constructor() {
          super();
          this.button = this.querySelector('.clickable-discount__btn');
          this.error = this.nextElementSibling?.matches('.clickable-discount__error')
            ? this.nextElementSibling
            : null;
          this.button?.addEventListener('click', () => this.apply());
        }

        async apply() {
          if (this.dataset.loading === 'true' || !this.dataset.code) return;
          this.dataset.loading = 'true';
          try {
            const response = await fetch(`${window.routes.cart_update_url}.js`, {
              ...window.fetchConfig('javascript'),
              body: JSON.stringify({
                discount: this.dataset.code,
                sections: ['cart-drawer', 'cart-icon-bubble'],
              }),
            });
            const cartData = await response.json();
            if (!response.ok || cartData.errors) throw new Error('Discount was not applied.');
            document.querySelector('cart-drawer')?.renderContents(cartData);
            this.dataset.applied = 'true';
            this.button?.setAttribute('aria-pressed', 'true');
            window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'clickable-discount', cartData });
          } catch (error) {
            this.dataset.error = 'true';
            if (this.error) this.error.hidden = false;
          } finally {
            this.dataset.loading = 'false';
          }
        }
      }
    );
  }

  class ProductUpsell extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        if (this.dataset.toggle !== 'true' || !event.target.closest('.upsell-toggle-btn')) return;
        event.preventDefault();
        this.toggle();
      });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('.variant-dropdown')) return;
        const variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
        const values = Array.from(this.querySelectorAll('.variant-dropdown')).map((select) => select.value);
        const variant = variants.find((item) => item.options?.every((value, index) => value === values[index]));
        if (variant) this.dataset.id = variant.id;
      });
    }

    async toggle() {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map((section) => section.section) || ['cart-icon-bubble'];
      const variantId = Number(this.dataset.id);
      if (!variantId) return;
      this.dataset.loading = 'true';
      try {
        const cart = await (await fetch('/cart.js')).json();
        const existing = cart.items.find((item) => Number(item.variant_id) === variantId);
        const response = existing
          ? await fetch(`${window.routes.cart_change_url}.js`, {
              ...window.fetchConfig('javascript'),
              body: JSON.stringify({ id: existing.key, quantity: 0, sections, sections_url: window.location.pathname }),
            })
          : await fetch(`${window.routes.cart_add_url}.js`, {
              ...window.fetchConfig('javascript'),
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname }),
            });
        const cartData = await response.json();
        if (!response.ok || cartData.errors) throw new Error('Upsell update failed.');
        this.dataset.selected = String(!existing);
        this.classList.toggle('upsell--selected', !existing);
        drawer?.renderContents(cartData);
        window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'upsell', cartData });
      } finally {
        this.dataset.loading = 'false';
      }
    }
  }

  if (!customElements.get('product-info-upsell')) customElements.define('product-info-upsell', ProductUpsell);
  if (!customElements.get('cart-drawer-upsell')) {
    customElements.define('cart-drawer-upsell', class CartDrawerUpsell extends ProductUpsell {});
  }

  if (!customElements.get('quantity-gifts')) {
    customElements.define(
      'quantity-gifts',
      class QuantityGifts extends HTMLElement {
        connectedCallback() {
          const form = document.getElementById(`product-form-${this.dataset.section}`);
          form?.querySelector('input[name="quantity"]')?.addEventListener('change', (event) => {
            this.querySelectorAll('.quantity-gift').forEach((gift) => {
              const unlocked = Number(event.target.value) >= Number(gift.dataset.quantity || 0);
              gift.dataset.unlocked = String(unlocked);
              gift.classList.toggle('quantity-gift--unlocked', unlocked);
            });
          });
        }
      }
    );
  }

  if (!customElements.get('cart-drawer-gift')) {
    customElements.define(
      'cart-drawer-gift',
      class CartDrawerGift extends HTMLElement {
        connectedCallback() {
          this.unsubscribe = window.subscribe?.(window.PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => this.reconcile(cartData));
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        async reconcile(cart) {
          if (!cart?.items || this.dataset.reconciling === 'true') return;
          const requirement = Number(this.dataset.requirement || 0);
          const eligible =
            this.dataset.type === 'cart_subtotal'
              ? cart.total_price >= requirement
              : cart.items
                  .filter((item) => !this.dataset.mainProduct || item.handle === this.dataset.mainProduct)
                  .reduce((total, item) => total + item.quantity, 0) >= requirement;
          const variantId = Number(this.dataset.id);
          const existing = cart.items.find((item) => Number(item.variant_id) === variantId);
          if (Boolean(existing) === eligible) return;

          this.dataset.reconciling = 'true';
          try {
            const drawer = document.querySelector('cart-drawer');
            const sections = drawer?.getSectionsToRender?.().map((section) => section.section) || ['cart-icon-bubble'];
            const response = existing
              ? await fetch(`${window.routes.cart_change_url}.js`, {
                  ...window.fetchConfig('javascript'),
                  body: JSON.stringify({ id: existing.key, quantity: 0, sections, sections_url: window.location.pathname }),
                })
              : await fetch(`${window.routes.cart_add_url}.js`, {
                  ...window.fetchConfig('javascript'),
                  body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname }),
                });
            const cartData = await response.json();
            if (!response.ok || cartData.errors) throw new Error('Gift update failed.');
            this.dataset.selected = String(eligible);
            drawer?.renderContents(cartData);
            window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-gift', cartData });
          } finally {
            this.dataset.reconciling = 'false';
          }
        }
      }
    );
  }

  class BundleDeals extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', (event) => {
        if (!event.target.matches('.bundle-deals__checkbox-js, .bundle-deals__variant-selects-js select')) return;
        this.updateSelection(event.target);
      });
    }

    updateSelection(target) {
      const item = target.closest('.bundle-deals__product-js') || target.closest('.bundle-deals__checkbox-js')?.parentElement;
      item?.classList.toggle('bundle-deals__product--deselected', target.matches('.bundle-deals__checkbox-js') && !target.checked);
      if (!target.matches('select')) return;
      const picker = target.closest('.bundle-deals__variant-selects-js');
      const variants = JSON.parse(picker?.querySelector('script[type="application/json"]')?.textContent || '[]');
      const values = Array.from(picker.querySelectorAll('select')).map((select) => select.value);
      const variant = variants.find((entry) => entry.options?.every((value, index) => value === values[index]));
      const checkbox = this.querySelector(`.bundle-deals__checkbox-js[data-index="${picker?.dataset.index}"]`);
      if (variant && checkbox) checkbox.dataset.id = variant.id;
    }

    getSelectedItems() {
      const grouped = new Map();
      this.querySelectorAll('.bundle-deals__checkbox-js').forEach((checkbox) => {
        if ('checked' in checkbox && !checkbox.checked) return;
        const id = Number(checkbox.dataset.id);
        if (!id) return;
        grouped.set(id, (grouped.get(id) || 0) + 1);
      });
      return Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
    }
  }

  if (!customElements.get('bundle-deals')) customElements.define('bundle-deals', BundleDeals);
  if (!customElements.get('main-bundle-offer')) {
    customElements.define(
      'main-bundle-offer',
      class MainBundleOffer extends BundleDeals {
        getSelectedItems() {
          const grouped = new Map(super.getSelectedItems().map((item) => [item.id, item.quantity]));
          this.querySelectorAll('.main-offer-item[data-selected="true"]').forEach((item) => {
            const id = Number(item.dataset.id);
            if (id) grouped.set(id, (grouped.get(id) || 0) + 1);
          });
          return Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
        }
      }
    );
  }

  if (!customElements.get('quantity-break-upsells')) {
    customElements.define(
      'quantity-break-upsells',
      class QuantityBreakUpsells extends HTMLElement {
        connectedCallback() {
          // Every `.quantity-break__upsell` rendered here already passed the
          // server-side `offer_index >= min_offer` check for this specific tier,
          // so it is unlocked by construction — no eligibility comparison needed.
          this.querySelectorAll('.quantity-break__upsell').forEach((item) => {
            item.dataset.unlocked = 'true';
            item.classList.toggle('quantity-break__upsell--selected', item.dataset.selected === 'true');
          });

          this.addEventListener('click', (event) => {
            const item = event.target.closest('.quantity-break__upsell');
            if (!item || item.dataset.toggle !== 'true') return;
            // Each upsell item is a plain <div> sitting inside its own tier's
            // <label for="quantity{i}-...">. Without preventDefault, clicking
            // it also activates that label's target radio natively — normally
            // harmless (it's already checked), but with every tier's copy of
            // the same gift visible at once (hide_upsells_on_non_selected off),
            // clicking a *different* tier's copy would silently switch the
            // selected "Buy N" tier out from under the customer.
            event.preventDefault();
            this.toggleSelection(item);
          });
        }

        toggleSelection(item) {
          const selected = item.dataset.selected !== 'true';
          item.dataset.selected = String(selected);
          item.classList.toggle('quantity-break__upsell--selected', selected);

          const quantityBreaks = document.querySelector(`quantity-breaks[data-section="${this.dataset.section}"]`);
          quantityBreaks?.updatePrices();
        }
      }
    );
  }
})();
